import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { changePassword } from '../../api/auth';
import { useAuth } from '../../app/AuthContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';

export function ProfilePage() {
  const { user } = useAuth();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      toast.show('Passwort geändert.', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiRequestError ? err.message : 'Ändern fehlgeschlagen.'),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
    mutation.mutate();
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-6">
      <PageHeader title="Mein Profil" />

      <Card>
        <CardHeader>
          <CardTitle>Konto</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col gap-2 text-sm">
          <p className="flex items-center justify-between">
            <span className="text-ivory-500">Benutzername</span> <span className="font-medium text-ivory-100">{user?.username}</span>
          </p>
          <p className="flex items-center justify-between">
            <span className="text-ivory-500">Rolle</span> <Badge tone="accent">{user?.role}</Badge>
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Passwort ändern</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input label="Aktuelles Passwort" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            <Input label="Neues Passwort" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={10} required />
            <Input label="Neues Passwort bestätigen" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            {error && <p className="text-sm text-danger-400">{error}</p>}
            <Button type="submit" loading={mutation.isPending} className="self-start">
              <KeyRound className="h-4 w-4" strokeWidth={1.75} /> Passwort ändern
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
